<template>
  <div
    v-if="order"
    :key="order.reference_id"
    class="pointer"
  >
    <v-layout
      row
      align-center
      @click="showOrder"
    >
      <v-flex
        xs10
        class="nowrap-text"
        :style="nameStyle"
      >
        {{ printDate(order.created_at) }}
      </v-flex>
      <v-flex
        xs2
        text-xs-right
      >
        <v-btn
          icon
          small
        >
          <v-icon
            :color="theme.btn_color_secondary"
            small
          >fa-chevron-right</v-icon>
        </v-btn>
      </v-flex>
    </v-layout>
    <v-layout
      row
      align-center
    >
      <v-flex
        xs12
        class="sub-text"
      >
        <span :style="nameStyle">{{ $t('status') }}:</span>
        <span class="status" :style="statusStyle(!order.is_refunded)">{{ !order.is_refunded ? $t('paid') : $t('refunded') }}</span>
      </v-flex>
    </v-layout>
    <v-layout
      v-if="!order.is_refunded"
      row
    >
      <v-flex
        v-if="order.queue_no"
        xs12
        class="sub-text"
      >
        <span :style="nameStyle">{{ $t('you_paid') }}</span>
        <span class="status" :style="statusStyle(true)">{{ order.payable }}</span>
        <span v-if="enableMemberPoints" :style="nameStyle">
          , {{ $t('used_points') }}:
          <span class="status" :style="statusStyle(true)">{{ used }}</span>
          , {{ $t('and_earned_points') }}:
          <span class="status" :style="statusStyle(true)">{{ earned }}</span>
        </span>
      </v-flex>
      <v-flex
        v-else
        xs12
        class="sub-text"
      >
        <span :style="nameStyle">{{ $t('you_paid') }}</span>
        <span class="status" :style="statusStyle(true)">{{ order.payable }}</span>
        <span v-if="enableMemberPoints" :style="nameStyle">
          , {{ $t('used_points') }}:
          <span class="status" :style="statusStyle(true)">{{ used }}</span>
        </span>
      </v-flex>
    </v-layout>
    <div v-if="withQueueNo">
      <v-layout
        v-if="order.queue_no"
        row
        align-center
      >
        <v-flex xs12>
          <span :style="nameStyle">{{ $t('queue_no') }}</span>
          <span class="status" :style="statusStyle(false)">{{ order.queue_no.slice(-3) }}</span>
        </v-flex>
      </v-layout>
      <v-layout
        v-else-if="this.isPending"
        row
        align-center
      >
        <v-flex>
          <span :style="nameStyle">{{ $t('waiting_for_number') }}:</span>
          <v-icon
            v-if="this.isLoading"
            :color="theme.orders_fail_color"
            small
            class="ml-2"
          >fas fa-circle-notch fa-spin</v-icon>
          <v-btn
            v-else
            dark
            class="sub-text"
            small
            :color="theme.btn_color_secondary"
            @click="getQueue"
          >{{ $t('get_queue_no') }}</v-btn>
        </v-flex>
      </v-layout>
    </div>
    <div>
      <v-layout
        row
        align-center
        v-if="canCancel && !order.is_refunded"
      >
        <v-flex>
          <v-btn
            dark
            class="sub-text"
            small
            :color="theme.btn_color_secondary"
            @click="confirmation('cancelOrder')"
          >Cancel</v-btn>
          <v-btn
            dark
            class="sub-text"
            small
            :color="theme.btn_color_secondary"
            @click="confirmation('editOrder')"
          >Edit</v-btn>
        </v-flex>
      </v-layout>
    </div>
    <feedback-message />
  </div>
</template>

<script>
import moment from 'moment'
import { mapGetters } from 'vuex'
import axios from 'axios'
import FeedbackMessage from './FeedbackMessage'

export default {
  components: { FeedbackMessage },
  props: {
    id: {
      type: Number,
      default: null
    },
    orderStyle: {
      type: Object,
      default: () => ({
        cursor: 'pointer'
      })
    },
    isSummaryMode: {
      type: Boolean,
      default: false
    },
    isPending: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      isLoading: false,
      canCancel: false,
      changeModeDialog: false,
      orderAddress: {
        id: '',
        title: '',
        postal_code: '',
        floor_unit: '',
        block_no: '',
        road_name: '',
        building: '',
        country: 'SINGAPORE',
        contact_name: '',
        contact_no: '',
        cutlery: '',
        remarks: ''
      }
    }
  },
  computed: {
    ...mapGetters('config', [
      'theme', 'withQueueNo', 'enableMemberPoints', 'modes', 'mode'
    ]),
    ...mapGetters('order', [
      'orders'
    ]),
    ...mapGetters('delivery', [
      'deliveryInfo'
    ]),
    ...mapGetters('cart', [
      'cartQty', 'pickupDate', 'pickupTime'
    ]),
    nameStyle () {
      return {
        color: this.theme.orders_name_color
      }
    },
    order () {
      return this.orders.find(o => parseInt(o.id) === this.id)
    },
    earned () {
      if (this.order && this.order.payments_topay > 0) {
        // return Math.floor(this.order.payments_topay / 20)
        if (this.order.earned !== null) {
          return parseFloat(Math.round(this.order.earned * 100) / 100).toFixed(2)
        }
      }
      return 0
    },
    used () {
      if (this.order && this.order.used) {
        return parseFloat(Math.round(this.order.used * 100) / 100).toFixed(2)
      }
      return parseFloat(Math.round(this.order.payments_points * 100) / 100).toFixed(2)
    }
  },
  methods: {
    statusStyle (success) {
      if (success) {
        return {
          color: this.theme.orders_success_color
        }
      }
      return {
        color: this.theme.orders_fail_color
      }
    },
    showOrder () {
      this.$router.push({ name: 'order', params: { id: this.order.id.toString() }})
    },
    async getQueue () {
      this.isLoading = true
      await this.retryFetchQueue()
      this.isLoading = false
    },
    async retryFetchQueue () {
      await this.$store.dispatch('order/fetchQueue', {
        reference_type: this.order.reference_type,
        reference_id: this.order.reference_id,
        amount: this.order.payments_topay
      })
      if (this.order.queue_no) {
        await this.$store.dispatch('order/fetchOrderPoints', {
          reference_id: this.order.reference_id,
          transaction_id: this.order.queue_no
        })
      }
    },
    printDate (d) {
      const d2 = moment(d)
      const today = moment()
      const yesterday = moment().subtract(1, 'day')
      if (d2.isSame(today, 'day')) return this.$t('today_at') + d2.format('h:mm A')
      if (d2.isSame(yesterday, 'day')) return this.$t('yesterday_at') + d2.format('h:mm A')
      return d2.format('D MMMM YYYY \\at h:mm A')
    },
    async confirmation(action) {
      let title, text
      switch (action) {
        case 'cancelOrder':
          title = 'Cancel Order'
          text = 'Do you want to cancel this order?'
          break;
        case 'editOrder':
          title = 'Cancel and Reorder'
          text = 'Do you want to cancel and reorder? Items in this order will be put into you cart.'
      }
      await this.$store.dispatch('responseMessage', {
        type: 'error',
        title: title,
        text: text,
        btn3: { text: 'Ok', function: action, param: this.order.id },
        modal: true
      })
    },
    async cancelOrder () {
      this.$bus.$emit('showFullLoading', true)
      let type, text = ''
      const refundStatus = this.refund()

      if (refundStatus) {
        type = 'success'
        text = 'Your order has been refunded.'
      } else {
        type = 'error'
        text = 'Failed to cancel order.'
      }

      this.$bus.$emit('showFullLoading', false)
      window.location.reload(true)
      await this.$store.dispatch('responseMessage', {
        type: type,
        text: text,
        timeout: 10000
      })
    },
    async editOrder () {
      //check order dine mode first, if different dine mode, prompt alert
      let orderMode = this.order.address ? this.modes.find(m => m.isDelivery) : this.modes.find(m => m.isTakeaway)
      if (this.mode.dineMode !== orderMode.dineMode) {
        await this.changeMode(orderMode)
      } else {
        // refund then put item into cart
        this.$bus.$emit('showFullLoading', true)
        let refundStatus = this.refund()
        if (refundStatus) {
          await this.putOrderToCart()
        }
        this.$bus.$emit('showFullLoading', false)
      }
    },
    async refund () {
      await axios.post('/api/order/refund', {
        reference_id: this.order.reference_id,
        reference_type: this.order.reference_type
      }).then(response => {
         return response.data === 'success'
      })
    },
    async putOrderToCart () {
      let items = JSON.parse(this.order.items)
      items.forEach(i => {
        this.$store.dispatch('cart/addBackCartItem', i)
      })
      await this.$store.dispatch('cart/fetchTakeawayDates')
      const orderPickupTime = this.order.pickup_time.split(' ')
      //set pickup date
      await this.$store.dispatch('cart/setPickupDate', orderPickupTime[0])
      //set pickup time
      await this.$store.dispatch('cart/fetchTakeawayTimes', this.pickupDate)
      const date = new Date(this.order.pickup_time);
      let hours = date.getHours();
      let minutes = date.getMinutes();
      let ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12;
      hours = hours ? hours : 12; // the hour '0' should be '12'
      hours = hours < 10 ? '0' + hours : hours
      minutes = minutes < 10 ? '0' + minutes : minutes
      const formattedTime = hours + ':' + minutes + ' ' + ampm;
      await this.$store.dispatch('cart/setPickupTime', formattedTime)
      // if delivery
      if (this.order.address) {
        //set remarks & cutlery
        await this.$store.dispatch('cart/setRemarks', this.orderAddress.remarks)
        await this.$store.dispatch('delivery/setAddressRemarks', this.orderAddress.remarks)
        if (this.orderAddress.cutlery) await this.$store.dispatch('delivery/setAddressCutlery', this.orderAddress.cutlery)
        await this.checkAddress()
        await this.$store.dispatch('delivery/setAddress', this.orderAddress)
      }
      this.$bus.$emit('showFullLoading', false)
      await this.$router.push({ name: 'cart' })
      await this.$store.dispatch('responseMessage', {
        type: 'success',
        text: 'Your order has been refunded.',
        timeout: 10000
      })
    },
    async checkAddress () {
      if (this.orderAddress.postal_code.length === 6) {
        await this.$store.dispatch('delivery/fetchDeliveryInfo', this.orderAddress.postal_code)
      } else {
        await this.$store.dispatch('delivery/resetDeliveryInfo')
      }
      const location = this.deliveryInfo.location
      this.orderAddress.block_no = location.block_no ? location.block_no : ''
      this.orderAddress.road_name = location.road_name ? location.road_name : ''
      this.orderAddress.building = location.building ? location.building : ''
    },
    changeMode (mode) {
      if (parseInt(mode.index) !== parseInt(this.mode.index)) {
        if (this.cartQty > 0) {
          const text = 'To proceed with this action, we will change your current dining mode, your selections in basket will be cleared. Are you sure?'
          this.$store.dispatch('responseMessage', {
            type: 'error',
            title: 'Start New Order',
            text: text,
            modal: true,
            buttons: [
              { text: 'Cancel' },
              { text: 'Ok', action: 'changeDiningMode', param: mode }
            ]
          })
        } else {
          this.$store.dispatch('changeDiningMode', mode)
          this.editOrder()
          // this.$router.push({ name: 'welcome' })
        }
      }
    },
  },
  async created () {
    if (this.order && !this.order.queue_no) {
      await this.retryFetchQueue()
    }
    if (this.order.queue_no && this.order.earned === null && this.order.used === null) {
      await this.$store.dispatch('order/fetchOrderPoints', {
        reference_id: this.order.reference_id,
        transaction_id: this.order.queue_no
      })
    }
    const client_refs = await axios.get('/api/epay_client_refs_new/' + this.order.reference_id + '/' + this.order.restaurant_id)
    const epay_client_refs = client_refs.data.epay_client_refs
    const remarks = epay_client_refs ? JSON.parse(epay_client_refs.email_content).remarks : null
    const cutlery = epay_client_refs ? JSON.parse(epay_client_refs.email_content).cutlery : null
    const receiver = epay_client_refs ? JSON.parse(epay_client_refs.email_content).receiver : null
    const receiver_contact = epay_client_refs ? JSON.parse(epay_client_refs.email_content).receiver_contact : null
    // set delivery address
    // address can be sent in variables
    if (this.order) {
      if (this.order.address) {
        let postalCode = this.order.address.match(/\b\d{6}\b/g)[0];

        this.orderAddress.id = ''
        this.orderAddress.title = ''
        this.orderAddress.postal_code = postalCode
        this.orderAddress.country = 'SINGAPORE'
        this.orderAddress.contact_name = receiver
        this.orderAddress.contact_no = receiver_contact
        this.orderAddress.cutlery = cutlery
        this.orderAddress.remarks = remarks
      }

      await axios.post('/api/order/check_cancel', {
        reference_id: this.order.reference_id,
        reference_type: this.order.reference_type
      }).then(response => {
        this.canCancel = response.data.result
      })
    }
  },
  beforeMount () {
    const vm = this
    this.$bus.$on('cancelOrder', (payload) => {
      if (this.order.id === payload) vm.cancelOrder()
    })
    this.$bus.$on('editOrder', (payload) => {
      if (this.order.id === payload) vm.editOrder()
    })
  },
  beforeDestroy () {
    this.$bus.$off('cancelOrder')
    this.$bus.$off('editOrder')
  }
}
</script>

<style scoped>
.pointer {
  cursor: pointer;
}
.status {
  font-weight: 500;
}
.nowrap-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
}
.sub-text {
  font-size: small;
}
</style>



// WEBPACK FOOTER //
// src/components/OrderLine.vue